/**
 * Nome utente in header con popup dei dettagli dell'identità (hover, focus o click).
 *
 * - Sessione TenaciousC: claim del JWT applicativo (ruoli, metodo di login, scadenza, issuer).
 * - Microsoft Entra ID (solo login Microsoft): TUTTI i claim dell'id_token ricevuto da Entra,
 *   letti dall'utente oidc-client-ts (readEntraProfile) — nessuna chiamata di rete né permesso
 *   Graph aggiuntivo. Esclusi solo i claim tecnici senza significato per l'utente (nonce, hash,
 *   identificativi interni di Microsoft).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { displayName, readEntraProfile, type JwtClaims } from '../auth/index.js';
import styles from './UserInfoPopover.module.css';

/** Etichette dei claim noti, nell'ordine di visualizzazione. */
const ENTRA_LABELS: [string, string][] = [
  ['name', 'Nome'],
  ['preferred_username', 'Utente (UPN)'],
  ['email', 'Email'],
  ['oid', 'Object ID utente'],
  ['tid', 'Tenant ID'],
  ['roles', 'App Role'],
  ['groups', 'Gruppi (object ID)'],
  ['idp', 'Identity provider'],
  ['sub', 'Subject (per questa app)'],
  ['aud', 'Audience (client ID)'],
  ['iss', 'Issuer'],
  ['ver', 'Versione token'],
  ['auth_time', 'Autenticato il'],
  ['iat', 'Emesso il'],
  ['nbf', 'Valido dal'],
  ['exp', 'Scade il'],
];

const TC_LABELS: [string, string][] = [
  ['sub', 'Utente'],
  ['email', 'Email'],
  ['roles', 'Ruoli TenaciousC'],
  ['amr', 'Metodo di login'],
  ['iss', 'Issuer'],
  ['iat', 'Emesso il'],
  ['exp', 'Scade il'],
];

/** Claim tecnici dell'id_token non mostrati (anti-replay, hash, ID interni Microsoft). */
const HIDDEN = new Set(['nonce', 'at_hash', 'c_hash', 'rh', 'uti', 'aio', 'sid', 's_hash']);
const TIME_CLAIMS = new Set(['auth_time', 'iat', 'nbf', 'exp']);
const METHOD_LABELS: Record<string, string> = {
  entra: 'Microsoft Entra ID',
  'break-glass': 'Amministratore locale',
};

function formatValue(key: string, v: unknown): string {
  if (TIME_CLAIMS.has(key) && typeof v === 'number') {
    return new Date(v * 1000).toLocaleString('it-IT');
  }
  if (key === 'amr' && Array.isArray(v)) {
    return v.map((m) => METHOD_LABELS[String(m)] ?? String(m)).join(', ');
  }
  if (Array.isArray(v)) return v.length ? v.map(String).join(', ') : '—';
  if (v !== null && typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Righe [chiave, etichetta, valore]: prima i claim noti, poi tutti gli altri in ordine alfabetico. */
export function claimRows(
  claims: Record<string, unknown>,
  labels: [string, string][],
  includeUnknown: boolean,
): [string, string, string][] {
  const known = new Map(labels);
  const rows: [string, string, string][] = labels
    .filter(([k]) => claims[k] !== undefined)
    .map(([k, label]) => [k, label, formatValue(k, claims[k])]);
  if (includeUnknown) {
    for (const k of Object.keys(claims).sort()) {
      if (known.has(k) || HIDDEN.has(k) || claims[k] === undefined) continue;
      rows.push([k, k, formatValue(k, claims[k])]);
    }
  }
  return rows;
}

function ClaimTable({ rows, testId }: { rows: [string, string, string][]; testId: string }) {
  return (
    <dl className={styles.claims} data-testid={testId}>
      {rows.map(([key, label, value]) => (
        <div key={key} className={styles.row} data-claim={key}>
          <dt title={key}>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function UserInfoPopover({ claims }: { claims: JwtClaims }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const popupId = useId();
  // Letto all'apertura: il profilo cambia a ogni rinnovo della sessione Entra.
  const entra = open ? readEntraProfile() : null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <span
      ref={wrapperRef}
      className={styles.wrapper}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(e) => {
        if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        className={styles.trigger}
        data-testid="header-user"
        aria-expanded={open}
        aria-controls={popupId}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen((o) => !o)}
      >
        {displayName(claims)}
      </button>
      {open && (
        <span className={styles.popupArea}>
          <div
            id={popupId}
            role="dialog"
            aria-label="Dettagli utente"
            className={styles.popup}
            data-testid="user-info-popup"
          >
            <p className={styles.sectionTitle}>Sessione TenaciousC</p>
            <ClaimTable rows={claimRows(claims, TC_LABELS, false)} testId="user-info-tc" />
            {entra ? (
              <>
                <p className={styles.sectionTitle}>Microsoft Entra ID (id_token)</p>
                <ClaimTable
                  rows={claimRows(entra.claims, ENTRA_LABELS, true)}
                  testId="user-info-entra"
                />
                {entra.scope && <p className={styles.footnote}>Scope concessi: {entra.scope}</p>}
              </>
            ) : (
              <p className={styles.footnote} data-testid="user-info-no-entra">
                Nessun dettaglio Microsoft Entra ID: accesso non effettuato con un account
                Microsoft.
              </p>
            )}
          </div>
        </span>
      )}
    </span>
  );
}
